import React, { useCallback, useEffect, useState } from 'react';
import { AnySchema } from 'yup';
import { SxStyle } from '../../../types';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { ColorResult, ChromePicker } from 'react-color';
import { InputWithValidateProps } from './text-input-with-validate';
import { useFieldValidation } from '../../../hooks/formValidate';

const styles: SxStyle = {
  container: {
    position: 'relative',
    width: '75px',
    alignItems: 'center',
    cursor: 'pointer',
  },
  popover: {
    position: 'absolute',
    top: 0,
    left: '85px',
    zIndex: 2,
  },
  cover: {
    position: 'fixed',
    top: '0px',
    right: '0px',
    bottom: '0px',
    left: '0px',
  },
  edit: {
    textDecoration: 'underline',
  },
  colorCard: {
    width: '75px',
    height: '60px',
  },
};

export type ColorPickerWithValidateProps<T> = InputWithValidateProps & {
  value?: string;
  name: keyof T & string;
  validation?: AnySchema;
  isReadOnly?: boolean;
};
function ColorPickerWithValidate<T>({
  value,
  name,
  onValue,
  onValidation,
  validation,
}: ColorPickerWithValidateProps<T>) {
  const { doValidate } = useFieldValidation();
  const [controlledValue, setControlledValue] = useState<string>(value ?? '');
  const [showColorPicker, setShowColorPicker] = useState(false);

  // TODO Show validation error message

  useEffect(() => {
    setControlledValue(value ?? '');
  }, [value]);

  useEffect(() => {
    const validate = async () => {
      if (validation) {
        const isValid = await doValidate(controlledValue, validation);
        onValidation?.(name, isValid, validation);
      }
    };
    validate();
  }, [controlledValue, doValidate, name, onValidation, validation]);

  const onChangeEvent = useCallback(
    (color: ColorResult) => {
      setControlledValue(color.hex);
      if (onValue) {
        onValue(name, color.hex);
      }
    },
    [name, onValue]
  );

  const combinedStyles = {
    ...styles.colorCard,
    ...{ backgroundColor: controlledValue },
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <Stack sx={styles.container} spacing={1} onClick={() => setShowColorPicker(true)}>
        <Card sx={combinedStyles} raised={true} elevation={4}></Card>
        <Typography variant="caption">{controlledValue}</Typography>
      </Stack>
      {showColorPicker && (
        <Box sx={styles.popover}>
          <Box sx={styles.cover} onClick={() => setShowColorPicker(false)} />
          <ChromePicker disableAlpha color={controlledValue} onChangeComplete={onChangeEvent} />
        </Box>
      )}
    </Box>
  );
}

export default ColorPickerWithValidate;
